import { FC } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ChoiceGroupQuestion, Cut, CutsheetCatalog } from '../../../types';
import { DropZone } from './drop-zone';
import { SortableItem } from './sortable-item';

export interface ChoiceGroupContentProps {
  question: ChoiceGroupQuestion;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddCutToOption?: (questionId: string, optionId: string) => void;
  onRemoveCutFromOption?: (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => void;
  /**
   * Called when the user drag-reorders cuts inside a choice option.
   * Receives the questionId, optionId, and the new ordered list of cut ids.
   */
  onReorderOptionCuts?: (
    questionId: string,
    optionId: string,
    newOrderIds: string[],
  ) => void;
}

// ---------------------------------------------------------------------------
// ChoiceGroupOption — isolated sub-component so each option gets its own
// sensor instance. dnd-kit sensors hold internal state and cannot be safely
// shared across multiple DndContext elements.
// ---------------------------------------------------------------------------

interface ChoiceGroupOptionProps {
  option: ChoiceGroupQuestion['options'][number];
  index: number;
  questionId: string;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddCutToOption?: (questionId: string, optionId: string) => void;
  onRemoveCutFromOption?: (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => void;
  onReorderOptionCuts?: (
    questionId: string,
    optionId: string,
    newOrderIds: string[],
  ) => void;
}

const ChoiceGroupOption: FC<ChoiceGroupOptionProps> = ({
  option,
  index,
  questionId,
  catalog,
  canAccept,
  draggingType,
  onAddCutToOption,
  onRemoveCutFromOption,
  onReorderOptionCuts,
}) => {
  // Each option has its own sensor instance — required when there are
  // multiple sibling DndContexts in the same component tree.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const optionCuts = option.cutIds
    .map((cutId) => catalog.cuts.find((cut) => cut.id === cutId))
    .filter((cut): cut is Cut => cut !== undefined);

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderOptionCuts) return;
    const oldIndex = option.cutIds.indexOf(active.id as string);
    const newIndex = option.cutIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderOptionCuts(
      questionId,
      option.id,
      arrayMove(option.cutIds, oldIndex, newIndex),
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
      }}
    >
      <Stack spacing={1.5}>
        {/* Option header */}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            Option {index + 1}: {option.label}
          </Typography>
          {option.description && (
            <Typography variant="caption" color="text.secondary">
              {option.description}
            </Typography>
          )}
        </Box>

        {/* Cuts assigned to this option — sortable via drag handle */}
        {optionCuts.length > 0 && (
          <Box>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleReorder}
            >
              <SortableContext
                items={optionCuts.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={0.5}>
                  {optionCuts.map((cut) => (
                    <SortableItem
                      key={cut.id}
                      item={cut}
                      itemType="cut"
                      onRemove={
                        onRemoveCutFromOption
                          ? () =>
                              onRemoveCutFromOption(
                                questionId,
                                option.id,
                                cut.id,
                              )
                          : undefined
                      }
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </Box>
        )}

        {/* Per-option dropzone — registers its own droppable ID so dnd-kit
            reports the correct optionId on drop from the outer context */}
        {onAddCutToOption && (
          <DropZone
            droppableId={`option-dropzone-${option.id}`}
            droppableData={{
              type: 'option-dropzone',
              questionId,
              optionId: option.id,
            }}
            canAccept={canAccept}
            draggingType={draggingType}
            incompatibleMessage="This question only accepts cuts"
            placeholder="Drop or click to add items"
            onClick={() => onAddCutToOption(questionId, option.id)}
          />
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Content panel for a ChoiceGroup question.
 *
 * Renders each choice option via `ChoiceGroupOption`, which is a dedicated
 * sub-component so every option gets its own dnd-kit sensor instance.
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.onAddCutToOption
 * @param root0.onRemoveCutFromOption
 * @param root0.onReorderOptionCuts
 */
export const ChoiceGroupContent: FC<ChoiceGroupContentProps> = ({
  question,
  catalog,
  canAccept,
  draggingType,
  onAddCutToOption,
  onRemoveCutFromOption,
  onReorderOptionCuts,
}) => {
  if (question.options.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {question.options.length} choice options
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {question.options.map((option, index) => (
        <ChoiceGroupOption
          key={option.id}
          option={option}
          index={index}
          questionId={question.id}
          catalog={catalog}
          canAccept={canAccept}
          draggingType={draggingType}
          onAddCutToOption={onAddCutToOption}
          onRemoveCutFromOption={onRemoveCutFromOption}
          onReorderOptionCuts={onReorderOptionCuts}
        />
      ))}
    </Stack>
  );
};
